import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLogout, getTokenUseCase } from "@/features/auth";
import { useMemberStore } from "@/features/auth/stores/member-store";

interface HeaderProps {
  onLogout?: () => void;
}

/**
 * GNB Header Widget - Global Navigation Bar
 *
 * Features:
 * - GGP Logo (Good Growth Planner)
 * - Service navigation links
 * - User account menu (MyPage, Login, Sign Up)
 * - Mobile responsive with hamburger menu
 */
export function Header({ onLogout }: HeaderProps) {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useMemberStore();

  // 인증 상태 확인 - user가 있거나 토큰이 있으면 인증된 상태
  const isAuthenticated = !!user || getTokenUseCase.hasToken();

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="w-full bg-white border-b border-black/10 sticky top-0 z-50">
      {/* Desktop Navigation */}
      <div className="hidden md:flex h-20 px-4 lg:px-20 xl:px-80 py-5 items-center justify-between">
        {/* Logo Section */}
        <div
          className="flex justify-start items-center cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate("/")}
        >
          <span className="text-orange-600 text-4xl font-extrabold font-['Pretendard']">
            G
          </span>
          <span className="text-yellow-400 text-4xl font-extrabold font-['Pretendard']">
            G
          </span>
          <span className="text-green-600 text-4xl font-extrabold font-['Pretendard']">
            P
          </span>
        </div>

        {/* Navigation Section */}
        <div className="flex justify-start items-center gap-8 lg:gap-40 flex-1 ml-12">
          {/* Service Navigation */}
          <div className="flex justify-start items-center gap-4 lg:gap-14">
            <nav
              className="text-black text-sm lg:text-lg font-normal font-['Pretendard'] cursor-pointer hover:font-bold transition-all whitespace-nowrap"
              onClick={() => navigate("/service")}
            >
              서비스 소개
            </nav>
            <nav
              className="text-black text-sm lg:text-lg font-normal font-['Pretendard'] cursor-pointer hover:font-bold transition-all whitespace-nowrap"
              onClick={() => navigate("/about")}
            >
              회사 소개
            </nav>
            <nav
              className="text-black text-sm lg:text-lg font-normal font-['Pretendard'] cursor-pointer hover:font-bold transition-all whitespace-nowrap"
              onClick={() => navigate("/subscription")}
            >
              구독
            </nav>
            <nav
              className="text-black text-sm lg:text-lg font-normal font-['Pretendard'] cursor-pointer hover:font-bold transition-all whitespace-nowrap"
              onClick={() => navigate("/support")}
            >
              고객지원
            </nav>
          </div>

          {/* User Account Menu */}
          <div className="flex justify-start items-center gap-4 lg:gap-10 ml-auto">
            {isAuthenticated ? (
              <>
                <nav
                  className="text-black/50 text-sm lg:text-lg font-normal font-['Pretendard'] cursor-pointer hover:text-black transition-colors whitespace-nowrap"
                  onClick={() => navigate("/mypage")}
                >
                  마이페이지
                </nav>
                <button
                  onClick={handleLogout}
                  className="text-black/50 text-sm lg:text-lg font-normal font-['Pretendard'] cursor-pointer hover:text-black transition-colors whitespace-nowrap"
                >
                  로그아웃
                </button>
              </>
            ) : (
              <>
                <nav
                  className="text-black/50 text-sm lg:text-lg font-normal font-['Pretendard'] cursor-pointer hover:text-black transition-colors whitespace-nowrap"
                  onClick={() => navigate("/login")}
                >
                  로그인
                </nav>
                <nav
                  className="text-black/50 text-sm lg:text-lg font-normal font-['Pretendard'] cursor-pointer hover:text-black transition-colors whitespace-nowrap"
                  onClick={() => navigate("/signup")}
                >
                  가입하기
                </nav>
              </>
            )}
            {onLogout && isAuthenticated && (
              <button
                onClick={handleLogout}
                className="text-black/50 text-sm lg:text-lg font-normal font-['Pretendard'] cursor-pointer hover:text-black transition-colors whitespace-nowrap"
              >
                로그아웃
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden h-16 px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <div
          className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => {
            navigate("/");
            closeMobileMenu();
          }}
        >
          <span className="text-orange-600 text-2xl font-extrabold font-['Pretendard']">
            G
          </span>
          <span className="text-yellow-400 text-2xl font-extrabold font-['Pretendard']">
            G
          </span>
          <span className="text-green-600 text-2xl font-extrabold font-['Pretendard']">
            P
          </span>
        </div>

        {/* Hamburger Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="메뉴"
        >
          <svg
            className="w-6 h-6 text-black"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {isMobileMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-black/10">
          <div className="px-4 py-4 space-y-3">
            {/* Service Navigation */}
            <nav
              className="block text-black text-base font-normal font-['Pretendard'] cursor-pointer hover:text-orange-600 transition-colors py-2"
              onClick={() => {
                navigate("/service");
                closeMobileMenu();
              }}
            >
              서비스 소개
            </nav>
            <nav
              className="block text-black text-base font-normal font-['Pretendard'] cursor-pointer hover:text-orange-600 transition-colors py-2"
              onClick={() => {
                navigate("/about");
                closeMobileMenu();
              }}
            >
              회사 소개
            </nav>
            <nav
              className="block text-black text-base font-normal font-['Pretendard'] cursor-pointer hover:text-orange-600 transition-colors py-2"
              onClick={() => {
                navigate("/subscription");
                closeMobileMenu();
              }}
            >
              구독
            </nav>
            <nav
              className="block text-black text-base font-normal font-['Pretendard'] cursor-pointer hover:text-orange-600 transition-colors py-2"
              onClick={() => {
                navigate("/support");
                closeMobileMenu();
              }}
            >
              고객지원
            </nav>

            {/* Divider */}
            <div className="border-t border-black/10 my-3"></div>

            {/* User Account Menu */}
            {isAuthenticated ? (
              <>
                <nav
                  className="block text-black/50 text-base font-normal font-['Pretendard'] cursor-pointer hover:text-black transition-colors py-2"
                  onClick={() => {
                    navigate("/mypage");
                    closeMobileMenu();
                  }}
                >
                  마이페이지
                </nav>
                <button
                  onClick={() => {
                    handleLogout();
                    closeMobileMenu();
                  }}
                  className="block w-full text-left text-black/50 text-base font-normal font-['Pretendard'] cursor-pointer hover:text-black transition-colors py-2"
                >
                  로그아웃
                </button>
              </>
            ) : (
              <>
                <nav
                  className="block text-black/50 text-base font-normal font-['Pretendard'] cursor-pointer hover:text-black transition-colors py-2"
                  onClick={() => {
                    navigate("/login");
                    closeMobileMenu();
                  }}
                >
                  로그인
                </nav>
                <nav
                  className="block text-black/50 text-base font-normal font-['Pretendard'] cursor-pointer hover:text-black transition-colors py-2"
                  onClick={() => {
                    navigate("/signup");
                    closeMobileMenu();
                  }}
                >
                  가입하기
                </nav>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
